import type { DerivedTuning, MovementTuning } from './tuning';
import type { V3 } from './vec';

/**
 * Predicted jump arc: forward-integrate a full-height jump from the current horizontal velocity
 * with the exact gravity rules the sim uses (rise/fall asymmetry + apex hang, jump held, no air
 * steering). Drawn as a polyline in the debug layer — it makes gap sizing self-evident, and the
 * tests use it as the max-jump-distance golden.
 *
 * Returned points are offsets from the launch feet position, one per fixed tick, ending when the
 * arc returns to launch height (flat-ground assumption).
 */
export function predictJumpArc(
  horizontalVel: V3,
  t: MovementTuning,
  d: DerivedTuning,
  fixedDt = 1 / 60,
  maxTicks = 150,
): V3[] {
  const points: V3[] = [{ x: 0, y: 0, z: 0 }];
  let x = 0;
  let y = 0;
  let z = 0;
  let vy = d.jumpVelocity;

  for (let i = 0; i < maxTicks; i++) {
    let g = vy > 0 ? d.gravityRise : d.gravityFall;
    if (Math.abs(vy) < t.apexHangVelThreshold) g *= t.apexHangGravityMul;
    vy = Math.max(vy - g * fixedDt, -t.terminalVelocity);
    x += horizontalVel.x * fixedDt;
    z += horizontalVel.z * fixedDt;
    y += vy * fixedDt;
    points.push({ x, y, z });
    if (y <= 0 && vy < 0) break;
  }
  return points;
}
